import {useMutation, useQuery, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

export type TriageAgentStatus = {
  enabled: boolean
}

function getQueryKey(apiUrl: string) {
  return ['triage-agent-status', apiUrl]
}

export function useTriageAgentStatus(repoOwner: string, repoName: string) {
  const apiUrl = `/${repoOwner}/${repoName}/triage-agent`

  const {data, isLoading, isError} = useQuery({
    queryKey: getQueryKey(apiUrl),
    queryFn: async () => {
      const response = await verifiedFetchJSON(apiUrl)
      if (!response.ok) {
        throw new Error('Failed to fetch triage agent status')
      }
      return response.json() as Promise<TriageAgentStatus>
    },
  })

  return {
    data,
    isLoading,
    isError,
  }
}

export function useUpdateTriageAgentStatus(repoOwner: string, repoName: string) {
  const queryClient = useQueryClient()
  const apiUrl = `/${repoOwner}/${repoName}/triage-agent`

  const {mutate, isPending, isError, reset} = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await verifiedFetchJSON(apiUrl, {
        method: 'PATCH',
        body: {enabled},
      })
      if (!response.ok) {
        throw new Error('Failed to update triage agent status')
      }
      return response.json() as Promise<TriageAgentStatus>
    },
    onMutate: async (newEnabled: boolean) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({queryKey: getQueryKey(apiUrl)})

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<TriageAgentStatus>(getQueryKey(apiUrl))

      // Optimistically update to the new value
      queryClient.setQueryData(getQueryKey(apiUrl), {enabled: newEnabled})

      // Return a context object with the snapshotted value
      return {previousData}
    },
    onError: (_err, _newEnabled, context) => {
      // Roll back to the previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(getQueryKey(apiUrl), context.previousData)
      }
    },
    onSettled: () => {
      // Refetch to sync with server state
      queryClient.invalidateQueries({queryKey: getQueryKey(apiUrl)})
    },
  })

  return {
    updateStatus: mutate,
    isPending,
    isError,
    reset,
  }
}
